import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL as string
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!url || !anonKey) {
  // Surfaced loudly — this is the single most common local-setup mistake.
  console.error(
    'VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY are missing.\n' +
    'Copy .env.example to .env.local and paste the values printed by `supabase start`.',
  )
}

export const supabase = createClient(url ?? '', anonKey ?? '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'oval-ims-auth',
  },
  global: {
    headers: { 'x-application-name': 'oval-ims' },
  },
})

export const FUNCTIONS_URL = `${url}/functions/v1`

/** Shown when the login this tab holds has been ended on the server. */
export const SESSION_ENDED_MESSAGE =
  'Your login on this screen has ended - the same account was signed out on another ' +
  'computer, phone or browser tab. Please sign out, sign in again, and retry.'

function postToFunction(name: string, body: unknown, token: string | undefined): Promise<Response> {
  return fetch(`${FUNCTIONS_URL}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token ?? anonKey}`,
      apikey: anonKey,
    },
    body: JSON.stringify(body),
  })
}

/**
 * Call an edge function, returning the raw Response so callers can stream downloads.
 *
 * Edge functions check the login with the Auth server (requireCaller ->
 * auth.getUser), which is stricter than the database: the database only checks
 * that the access token is signed, the Auth server also checks that its login
 * session still exists. When the same account signs out somewhere else, every
 * screen keeps reading data for up to an hour but every export/PDF fails with
 * "Not authenticated" (seen 11-09-2026, Head of Stores, consumption export).
 *
 * So on an authentication refusal we try ONCE to get a fresh token and resend.
 * That cures an access token that simply expired. If the session itself is
 * gone, refreshing fails too, and the caller gets a plain sentence instead of
 * "Not authenticated (Error: Not authenticated)".
 */
export async function callFunction(name: string, body: unknown): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await postToFunction(name, body, session?.access_token)
  if (!session || res.ok || (res.status !== 401 && res.status !== 500)) return res

  const text = await res.clone().text().catch(() => '')
  const authRefusal = res.status === 401 || /not authenticated|invalid jwt|jwt expired/i.test(text)
  if (!authRefusal) return res

  const { data: fresh, error } = await supabase.auth.refreshSession()
  if (error || !fresh.session) {
    return new Response(JSON.stringify({ error: SESSION_ENDED_MESSAGE }), {
      status: 401, headers: { 'Content-Type': 'application/json' },
    })
  }
  const retry = await postToFunction(name, body, fresh.session.access_token)
  if (retry.status === 401 || (retry.status === 500 && /not authenticated/i.test(await retry.clone().text().catch(() => '')))) {
    return new Response(JSON.stringify({ error: SESSION_ENDED_MESSAGE }), {
      status: 401, headers: { 'Content-Type': 'application/json' },
    })
  }
  return retry
}

/** Call an edge function and download whatever it returns as a file. */
export async function downloadFromFunction(name: string, body: unknown, fallbackName: string) {
  const res = await callFunction(name, body)
  if (!res.ok) {
    // Every edge function refuses with {"error":"a sentence for the user"}.
    // Throwing the raw body put the JSON braces on screen —
    //   {"error":"No opening stock has been loaded yet, ..."}
    // — which reads like a crash rather than the plain refusal it is. Unwrap
    // it. Anything that is not our JSON shape is passed through unchanged,
    // so a genuine server failure is still reported verbatim.
    const text = (await res.text()).trim()
    let message = text
    try {
      const parsed = JSON.parse(text)
      if (parsed && typeof parsed.error === 'string') {
        message = parsed.detail ? `${parsed.error} (${parsed.detail})` : parsed.error
      }
    } catch { /* not JSON — show what the server actually said */ }
    throw new Error(message || `${name} failed with ${res.status}`)
  }
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  const blob = await res.blob()
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = match?.[1] ?? fallbackName
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(link.href), 4000)
}
